import { Timestamp } from 'firebase/firestore';
import { useTime } from 'src/composables/time';

/**
 * Translating between the calendar's date strings and the Timestamps a statement stores.
 *
 * Moved out of the dialog. The end-of-day adjustment here is the part that matters: a range stored
 * as midnight-to-midnight excludes everything that happened on its own last day.
 */

const CALENDAR_FORMAT = 'yyyy-MM-dd';
const CALENDAR_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
/*
 * To the last millisecond, not the last whole second. The bound is compared with <=, so stopping
 * at 23:59:59.000 drops anything recorded in the final second of the chosen day.
 */
const END_OF_DAY_TIME = '23:59:59';
const END_OF_DAY_MILLISECONDS = 999;

const toCalendarString = (value) => {
  if (!(value instanceof Timestamp)) {
    return null;
  }

  const { formatDate } = useTime();

  return formatDate(value, CALENDAR_FORMAT);
};

/*
 * The bound is resolved in the user's timezone, so a day chosen in the calendar covers that day
 * where the user is rather than where their device is. A half-picked range is rejected rather than
 * stored: anything unparsable comes back as an invalid Date, which would otherwise count as a
 * complete enough bound to apply.
 */
const atTimeOfDay = (calendarDate, timeOfDay, milliseconds = 0) => {
  if (!CALENDAR_PATTERN.test(calendarDate ?? '')) {
    return null;
  }

  const { extractDate, formatDate } = useTime();

  const parsed = extractDate(`${calendarDate} ${timeOfDay}`, `${CALENDAR_FORMAT} HH:mm:ss`);
  if (!(parsed instanceof Date) || Number.isNaN(parsed.getTime())) {
    return null;
  }

  parsed.setMilliseconds(milliseconds);

  /*
   * A wall clock the timezone skipped is resolved rather than rejected, and that can land on a
   * different day: Pacific/Apia never had a 30 Dec 2011, so midnight on it comes back as the 31st.
   * Formatting it back is what catches a bound that moved off the day it was picked on.
   */
  return formatDate(parsed, CALENDAR_FORMAT) === calendarDate ? Timestamp.fromDate(parsed) : null;
};

const startOfDay = (calendarDate) => atTimeOfDay(calendarDate, '00:00:00');

const endOfDay = (calendarDate) => atTimeOfDay(
  calendarDate,
  END_OF_DAY_TIME,
  END_OF_DAY_MILLISECONDS,
);

/**
 * The individually chosen dates, as the calendar wants them.
 *
 * @param {object} statement
 * @returns {string[]}
 */
export function toCalendarDates(statement) {
  const stored = statement?.filterDateItems;

  return (Array.isArray(stored) ? stored : [])
    .map(toCalendarString)
    .filter(Boolean);
}

/**
 * The fields to write when the user picks individual dates.
 *
 * The bounds are derived rather than asked for: the earliest date opens the range and the latest
 * closes it, extended to the end of that day so the day itself is included.
 *
 * @param {string[]} calendarDates
 * @returns {{ filterDateItems: Timestamp[], fromDate: Timestamp|null, toDate: Timestamp|null }}
 */
export function fromCalendarDates(calendarDates) {
  const filterDateItems = (calendarDates ?? [])
    .map((calendarDate) => startOfDay(calendarDate))
    .filter(Boolean);

  if (filterDateItems.length === 0) {
    return { filterDateItems, fromDate: null, toDate: null };
  }

  const millis = filterDateItems.map((timestamp) => timestamp.toMillis());
  const earliest = filterDateItems[millis.indexOf(Math.min(...millis))];
  const latest = filterDateItems[millis.indexOf(Math.max(...millis))];

  return {
    filterDateItems,
    fromDate: earliest,
    toDate: endOfDay(toCalendarString(latest)),
  };
}

/**
 * The chosen range, as the calendar wants it, or null when no range is set.
 *
 * @param {object} statement
 * @returns {{ from: string, to: string }|null}
 */
export function toCalendarRange(statement) {
  const from = toCalendarString(statement?.fromDate);
  const to = toCalendarString(statement?.toDate);

  return from && to ? { from, to } : null;
}

/**
 * The fields to write when the user picks a range. The range opens at the start of its first day
 * and closes at the end of its last.
 *
 * @param {{ from: string, to: string }|null} range
 * @returns {{ filterDateItems: [], fromDate: Timestamp|null, toDate: Timestamp|null }}
 */
export function fromCalendarRange(range) {
  if (!range) {
    return { filterDateItems: [], fromDate: null, toDate: null };
  }

  return {
    filterDateItems: [],
    fromDate: startOfDay(range.from),
    toDate: endOfDay(range.to),
  };
}

/**
 * The fields to write when the user picks a named preset. The preset carries its own resolved
 * bounds, so the individually chosen dates are dropped.
 *
 * @param {{ fromTimestamp?: Date, toTimestamp?: Date }} [preset]
 * @returns {{ filterDateItems: [], fromDate: Timestamp|null, toDate: Timestamp|null }}
 */
export function fromDatePreset(preset) {
  if (!preset?.fromTimestamp || !preset?.toTimestamp) {
    return { filterDateItems: [], fromDate: null, toDate: null };
  }

  return {
    filterDateItems: [],
    fromDate: Timestamp.fromDate(preset.fromTimestamp),
    toDate: Timestamp.fromDate(preset.toTimestamp),
  };
}

/**
 * The fields to write when the user switches between comparing against a window and comparing
 * against a cutoff. The two operators store their answer in different fields, so everything the
 * previous one filled in is cleared: left behind, a stale preset or bound would apply alongside
 * the new operator.
 *
 * @param {string} dateOperator
 * @returns {object}
 */
export function fromDateOperator(dateOperator) {
  return {
    dateOperator,
    filterCriteriaType: null,
    filterDateItems: [],
    fromDate: null,
    toDate: null,
    filteredItems: null,
  };
}
